/**
 * ProShip Direct v2 — what switching ProShip couriers off is costing.
 *
 * A merchant on Direct alone is choosing a smaller network, and the panel should say what the
 * choice costs rather than leaving them to discover it through failed orders. Three questions,
 * each answerable from data the panel already holds:
 *
 *   REACH — how many delivery pincodes their own contracts cover, against ProShip's network.
 *   WASTE — how many shipments are sitting unprocessed on a coverage failure, which is the one
 *           failure a wider network would have absorbed.
 *   GAPS  — which service lines ProShip carries that none of their own accounts do.
 *
 * EVERY NUMBER HERE IS REAL OR ABSENT. No fixture stands in: the reach figures come from the
 * serviceability-intel API, the unprocessed count from order/search, the service lines from the
 * courier catalogue. Where a call returns nothing — the intel endpoints are role-gated and may
 * give a merchant nothing at all — the card says the figure is unavailable instead of inventing
 * one. A made-up gap would be an argument for buying something, which is the worst possible thing
 * to fabricate.
 */

/**
 * Failure categories a wider courier network could plausibly have absorbed.
 *
 * Taken from FAILURE_CATEGORY_INFO in components/orders/list2/StuckOrders.js — the same vocabulary
 * the Unprocessed Shipments page reads, so the two screens cannot disagree about what a reason
 * means.
 *
 * The test for inclusion is narrow: the order failed because NO courier in use covers the lane.
 * Wallet limits, bad dimensions, courier API errors and rule mismatches are all excluded — a
 * bigger network fixes none of them, and counting them would inflate the number in ProShip's
 * favour, which is exactly the number a merchant should be able to trust.
 */
export const COVERAGE_FAILURE_CATEGORIES = [
  'PINCODE_NOT_SERVICEABLE',
  'PICKUP_PIN_NOT_SERVICEABLE',
  'DROP_PIN_NOT_SERVICEABLE',
  'ZONE_NOT_COVERED',
  'FULL_COVERAGE_ABSENT',
  'COURIER_API_NOT_SERVICEABLE',
];

export const isCoverageFailure = (category) =>
  COVERAGE_FAILURE_CATEGORIES.includes(String(category || '').toUpperCase());

/**
 * Distinct pincodes across every uploaded coverage file.
 *
 * A union, not a sum. Two accounts both covering Bengaluru is one pincode reached, and adding
 * their counts would claim reach the merchant does not have — the error grows with every account
 * they add, so it would look best exactly when it is most wrong.
 *
 * `files` is the serviceabilityLanes slice: {[accountId]: {rows: [{pickupPincode, deliveryPincode}]}}.
 */
export function ownCoverage(files) {
  const delivery = new Set();
  const pickup = new Set();

  Object.values(files || {}).forEach((file) => {
    (file?.rows || []).forEach((row) => {
      if (row.deliveryPincode) delivery.add(String(row.deliveryPincode).trim());
      if (row.pickupPincode) pickup.add(String(row.pickupPincode).trim());
    });
  });

  return {
    delivery: delivery.size,
    pickup: pickup.size,
    accounts: Object.keys(files || {}).length,
  };
}

/**
 * Service lines the merchant runs, against the ones ProShip carries.
 *
 * A "service line" is the service type on a courier account — Surface, Air, Same Day, and so on.
 * Mine counts only accounts that can actually ship: an account switched off, or one whose
 * credentials never passed, is not a line the merchant has however it looks in the list.
 *
 * ProShip's side counts every ProShip courier in the catalogue, enabled or not. The question is
 * what the network can carry, not what is switched on right now — a line the merchant has turned
 * off is still a line available to them, and that is the point of showing it.
 */
export function serviceLineGap(rows, { OWNER, CREDENTIAL_STATE }) {
  const clean = (value) => String(value || '').trim().toUpperCase();

  const mine = new Set();
  const proship = new Set();

  (rows || []).forEach((row) => {
    const line = clean(row.serviceType);
    if (!line) return;

    if (row.owner === OWNER.DIRECT) {
      const shippable = row.enabled && row.credentials === CREDENTIAL_STATE.TESTED;
      if (shippable) mine.add(line);
    } else if (row.owner === OWNER.PROSHIP) {
      proship.add(line);
    }
  });

  const missing = [...proship].filter((line) => !mine.has(line)).sort();

  return {
    mine: [...mine].sort(),
    proship: [...proship].sort(),
    missing,
  };
}

/**
 * The reach gap, once both sides are known.
 *
 * Returns null when ProShip's figure is missing rather than guessing at it, and null again when
 * ProShip covers no more than the merchant already does — a gap of zero or less is not a finding
 * worth a card, and "0 more pincodes" reads as a broken number rather than good news.
 */
export function reachGap(own, proshipPincodes) {
  if (!proshipPincodes) return null;
  const extra = proshipPincodes - own.delivery;
  if (extra <= 0) return null;
  return {
    own: own.delivery,
    proship: proshipPincodes,
    extra,
    // Share of ProShip's network the merchant's own contracts reach. Guarded: a merchant with no
    // coverage uploaded at all is at 0, not NaN.
    shareOfNetwork: proshipPincodes > 0 ? Math.round((own.delivery / proshipPincodes) * 100) : 0,
  };
}

/**
 * The findings as compact stat cards, for the row of them on Home.
 *
 * DESCRIPTORS, NOT COMPONENTS. These sit in the same row as "Your courier contracts" and must look
 * identical to it; returning data the page renders through its own card keeps that true by
 * construction rather than by two sets of styles agreeing.
 *
 * `tooltip` is where the detail went. As full-width cards these carried a paragraph each, which is
 * more than the finding is worth on a page a merchant is passing through — the number earns the
 * space, the caveat earns a hover.
 *
 * `tone` is a name, not a colour. This file has no business importing a palette — the page owns
 * how a tone is painted, the same way it owns the card.
 */
export function opportunityStats({ reach, unprocessed, lines, proshipOn }) {
  const n = (value) => Number(value || 0).toLocaleString('en-IN');
  const cards = [];

  /*
    Reach is claimed only when EVERY ProShip courier is off. With some still on, the merchant does
    reach those pincodes today, and "cannot reach" would simply be false.
  */
  if (proshipOn === 0 && reach) {
    cards.push({
      label: 'Pincodes you cannot reach',
      value: `+${n(reach.extra)}`,
      sub: `${reach.shareOfNetwork}% of ProShip’s network covered`,
      tone: 'INFO',
      to: '/courier-availability',
      tooltip:
        `Your uploaded coverage reaches ${n(reach.own)} delivery pincodes; ProShip’s couriers reach ` +
        `${n(reach.proship)}. Turning them on adds ${n(reach.extra)}. Your figure counts each pincode ` +
        'once across all accounts, so two couriers covering the same city count once.',
    });
  }

  if (!unprocessed.loading && unprocessed.total > 0) {
    const share = unprocessed.inspected > 0
      ? Math.round((unprocessed.coverage / unprocessed.inspected) * 100)
      : 0;
    cards.push({
      label: 'Unprocessed shipments',
      value: n(unprocessed.total),
      sub: unprocessed.coverage > 0
        ? `${unprocessed.sampled ? `~${share}%` : n(unprocessed.coverage)} failed on coverage`
        : 'none failed on coverage',
      tone: 'WARN',
      to: '/stuck-orders',
      absolute: true,
      tooltip: unprocessed.coverage > 0
        ? `${unprocessed.sampled ? `About ${share}% of the ${n(unprocessed.inspected)} most recent` : `${n(unprocessed.coverage)} of these`} ` +
          'failed because no courier you use covers the lane — a wider network may have carried them. ' +
          'Whether ProShip serves each of those pincodes is a serviceability check this figure has not run.'
        : 'These failed on wallet limits, order data or courier errors — reasons a wider courier network would not have fixed.',
    });
  }

  if (lines.missing.length > 0) {
    cards.push({
      label: 'Service lines',
      value: `${lines.mine.length} / ${lines.mine.length + lines.missing.length}`,
      sub: `${lines.missing.length} ProShip carries, you do not`,
      to: '/couriers/connect',
      tooltip:
        `Direct: ${lines.mine.join(', ') || 'none shipping yet'}. ` +
        `Missing: ${lines.missing.join(', ')}. ` +
        'Direct counts only accounts switched on with credentials passed — an order needing a missing ' +
        'line has nowhere to go on your own contracts.',
    });
  }

  return cards;
}
